interface BoardBowl {
  readonly id: number;
  readonly open: boolean;
  /** null = covered/masked (must not render a count). */
  readonly count: number | null;
}

interface MemoryFeastBoardViewProps {
  readonly bowls: readonly BoardBowl[];
  readonly lastOpen?: {
    readonly bowlIds: readonly number[];
    readonly matched: boolean;
  } | null;
}

/**
 * Read-only 5×4 bowl grid for 기억의 만찬. A covered bowl renders only its
 * public number — its token count is NEVER placed in the DOM (the count span is
 * rendered only when `count !== null`), preserving the hidden-information
 * contract. The current open pair is ringed (green match / red mismatch).
 */
export function MemoryFeastBoardView({
  bowls,
  lastOpen,
}: MemoryFeastBoardViewProps) {
  const inLastOpen = new Set(lastOpen?.bowlIds ?? []);

  return (
    <div
      data-testid="memory-feast-board"
      className="grid w-full select-none gap-2 rounded-lg p-3"
      style={{
        gridTemplateColumns: "repeat(5, 1fr)",
        background: "var(--cn-memory-feast-table)",
        maxWidth: "min(94vw, 480px)",
      }}
    >
      {bowls.map((bowl) => {
        const revealed = bowl.count !== null;
        const highlighted = inLastOpen.has(bowl.id);
        const ring = highlighted
          ? lastOpen?.matched
            ? "var(--cn-memory-feast-match)"
            : "var(--cn-memory-feast-miss)"
          : "var(--cn-memory-feast-bowl-edge)";
        return (
          <div
            key={bowl.id}
            data-testid={`memory-feast-bowl-${bowl.id}`}
            data-state={revealed ? "open" : "covered"}
            className="relative flex aspect-square flex-col items-center justify-center rounded-full"
            style={{
              background: revealed
                ? "var(--cn-memory-feast-bowl-open)"
                : "var(--cn-memory-feast-bowl)",
              boxShadow: `inset 0 0 0 ${highlighted ? "3px" : "2px"} ${ring}`,
            }}
          >
            <span
              className="absolute top-1 left-2 font-semibold text-[10px]"
              style={{ color: "var(--cn-memory-feast-label-text)" }}
            >
              {bowl.id + 1}
            </span>
            {revealed ? (
              <span
                data-testid="memory-feast-bowl-count"
                className="font-bold text-xl"
                style={{ color: "var(--cn-memory-feast-count)" }}
              >
                {bowl.count}
              </span>
            ) : (
              <span
                aria-hidden="true"
                className="text-lg"
                style={{ color: "var(--cn-memory-feast-bowl-edge)" }}
              >
                ●
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
